import {
  collection, doc, query, orderBy, limit, onSnapshot, addDoc, setDoc,
  getDoc, getDocs, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase-config';

/**
 * Chat v2 — the clean data layer under the rebuilt chat surface (ChatV2).
 *
 * SAME Firestore data as the legacy chat, by design, so the two UIs
 * interoperate and switching between them loses nothing:
 *   chats/{chatId}                      — { type, participants, createdAt }
 *   chats/{chatId}/messages             — { text, createdAt, user, uid, clientId }
 *   users/{uid}/chats/{chatId}          — the per-user chat list row
 *                                         { users: [{id, displayName, email}],
 *                                           updatedAt, lastMessage, lastUser,
 *                                           chatLength, type }
 *   users/{uid}/updatewatchList/{chatId}— the read watermark { chatId, chatLength, updatedAt }
 *
 * What's DIFFERENT from legacy is the shape of the reads: messages are a
 * single live query of the LAST 50 (legacy loaded the entire history), the
 * list fan-out is one parallel batch (legacy looped with timers), and every
 * subscription is one onSnapshot with client-side sorting — no polling, no
 * cascading effects.
 */

const myUid = () => auth.currentUser?.uid || null;

/** Live list of my chats, newest activity first. Returns unsubscribe. */
export const watchMyChats = (cb) => {
  const uid = myUid();
  if (!uid) return () => {};
  return onSnapshot(collection(db, 'users', uid, 'chats'), (snap) => {
    const rows = [];
    snap.forEach((d) => rows.push({ chatId: d.id, ...d.data() }));
    rows.sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0));
    cb(rows);
  }, (e) => console.warn('[chatV2] chat list listener:', e?.message));
};

/** Live read-watermarks map { chatId: chatLength }. Returns unsubscribe. */
export const watchReadMarks = (cb) => {
  const uid = myUid();
  if (!uid) return () => {};
  return onSnapshot(collection(db, 'users', uid, 'updatewatchList'), (snap) => {
    const map = {};
    snap.forEach((d) => { map[d.id] = d.data()?.chatLength ?? 0; });
    cb(map);
  }, (e) => console.warn('[chatV2] read marks listener:', e?.message));
};

/** Live LAST-50 messages of one chat, oldest→newest. Returns unsubscribe. */
export const watchMessages = (chatId, cb) => {
  if (!chatId) return () => {};
  const qy = query(
    collection(db, 'chats', chatId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  return onSnapshot(qy, (snap) => {
    const rows = [];
    snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
    // Pending local writes carry a null serverTimestamp — sort them last
    // (they ARE the newest) instead of letting null float to the top.
    rows.sort((a, b) =>
      (a.createdAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER)
      - (b.createdAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER));
    cb(rows);
  }, (e) => console.warn('[chatV2] messages listener:', e?.message));
};

/**
 * Send a message. Writes the SAME message shape legacy writes, then fans the
 * activity stamp out to every participant's chat-list row in one parallel
 * batch so both chat UIs (and the push notifier) light up.
 */
export const sendChatMessage = async (chatId, { text, displayName, clientId, users, newLength }) => {
  const uid = myUid();
  const body = String(text || '').trim();
  if (!uid || !chatId || !body) return false;
  await addDoc(collection(db, 'chats', chatId, 'messages'), {
    text: body,
    createdAt: serverTimestamp(),
    user: displayName || 'Guest',
    uid,
    clientId: clientId || null,
  });
  const patch = {
    updatedAt: serverTimestamp(),
    lastMessage: body,
    lastUser: uid,
    chatLength: newLength ?? 0,
  };
  await Promise.all((users || []).map((u) =>
    updateDoc(doc(db, 'users', u.id, 'chats', chatId), patch).catch(() => {})
  ));
  return true;
};

/** Stamp my read watermark for a chat (same doc legacy maintains). */
export const markChatRead = async (chatId, chatLength) => {
  const uid = myUid();
  if (!uid || !chatId) return;
  await setDoc(doc(db, 'users', uid, 'updatewatchList', chatId), {
    chatId, chatLength: chatLength ?? 0, updatedAt: serverTimestamp(),
  }).catch(() => {});
};

/** Keep chats/{chatId}.participants populated (the push notifier reads it). */
export const ensureParticipants = async (chatId, users) => {
  const ids = (users || []).map((u) => u.id).filter(Boolean);
  if (!chatId || !ids.length) return;
  await setDoc(doc(db, 'chats', chatId), { participants: ids }, { merge: true }).catch(() => {});
};

/**
 * Find my existing 1:1 chat with `other`, or create one — the same shape
 * Users4's start-chat flow writes, so either surface can open it.
 * Returns { chatId, users } ready for the conversation view.
 */
export const startChatWith = async (me, other) => {
  const uid = myUid();
  if (!uid || !other?.id) return null;
  const meProfile = { id: uid, email: me?.email || '', displayName: me?.displayName || '' };
  const otherProfile = { id: other.id, email: other.email || '', displayName: other.displayName || '' };

  const { docs } = await getDocs(collection(db, 'users', uid, 'chats'));
  let chatId = null;
  docs.forEach((d) => {
    const list = d.data().users || [];
    if (list.some((cu) => cu && cu.id === other.id)) chatId = d.id;
  });

  if (!chatId) {
    const newChat = await addDoc(collection(db, 'chats'), {
      createdAt: serverTimestamp(),
      type: 'private',
      participants: [uid, other.id],
    });
    chatId = newChat.id;
    await Promise.all([
      setDoc(doc(db, 'users', uid, 'chats', chatId), {
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        type: 'private', newchat: 1, users: [meProfile, otherProfile], chatId,
      }),
      setDoc(doc(db, 'users', other.id, 'chats', chatId), {
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        type: 'private', users: [otherProfile, meProfile], chatId,
      }),
    ]);
  }
  return { chatId, users: [meProfile, otherProfile] };
};

/** users/{uid} profile fetch with an in-memory cache (avatars, names). */
const profileCache = new Map();
export const getProfile = async (uid) => {
  if (!uid) return null;
  if (profileCache.has(uid)) return profileCache.get(uid);
  let out = null;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      out = {
        displayName: snap.data().displayName || null,
        photoURL: snap.data().photoURL || null,
      };
    }
  } catch (_) {}
  profileCache.set(uid, out);
  return out;
};
